/**
 * ValueTree.tsx — a read-only view of anything the Engine returned.
 *
 * The Engine's results carry facts this repository does not own: their field
 * names, their nesting, their types. A component with a hard-coded shape for
 * them would be a second description of the Engine's result — the thing
 * `capabilityManifest.ts` exists to remove — and it would break the first time
 * the Engine added a field.
 *
 * So this renders a bounded, generic tree. It is deliberately READ-ONLY. There
 * is no control here that could write, because a viewer that can edit what it
 * is displaying is a viewer that will eventually be mistaken for a form.
 *
 * `schema`, when supplied, is used for two things only: to label a property with
 * the Engine's own `description`, and to mark the ones the Engine declared
 * `primary`. Neither changes a value and neither decides anything.
 */

import type { JsonSchemaFragment } from "../../api/capabilityManifest";

/** Bounded so a hostile or accidental cycle cannot hang the workbench. */
const MAX_DEPTH = 8;
const MAX_CHILDREN = 200;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function propertiesOf(schema: JsonSchemaFragment | undefined): Record<string, JsonSchemaFragment> {
  const raw = schema?.properties;
  return isRecord(raw) ? (raw as Record<string, JsonSchemaFragment>) : {};
}

/** The Engine's own description for a property, when it published one. */
function descriptionOf(schema: JsonSchemaFragment | undefined): string | undefined {
  return typeof schema?.description === "string" && schema.description !== "" ? schema.description : undefined;
}

function scalar(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return String(value);
}

function Node({ name, value, schema, depth, highlight }: {
  name: string | undefined;
  value: unknown;
  schema: JsonSchemaFragment | undefined;
  depth: number;
  highlight: readonly string[];
}): JSX.Element {
  if (depth > MAX_DEPTH) {
    return <li className="value-leaf"><span className="muted">… nested further than this viewer shows</span></li>;
  }
  const properties = propertiesOf(schema);
  const childSchema = name === undefined ? undefined : properties[name];
  const description = descriptionOf(childSchema);
  const isPrimary = name !== undefined && highlight.includes(name);

  if (Array.isArray(value)) {
    return (
      <li className="value-node">
        <span className="value-name">{name ?? "(root)"}</span>
        {description && <span className="muted">{description}</span>}
        <span className="muted">{value.length} entries</span>
        <ul>
          {value.slice(0, MAX_CHILDREN).map((entry, index) => (
            <Node key={index} name={undefined} value={entry} schema={childSchema} depth={depth + 1} highlight={highlight} />
          ))}
          {value.length > MAX_CHILDREN && <li className="muted">… {value.length - MAX_CHILDREN} more</li>}
        </ul>
      </li>
    );
  }

  if (isRecord(value)) {
    return (
      <li className={isPrimary ? "value-node is-primary" : "value-node"}>
        <span className="value-name">{name ?? "(root)"}</span>
        {description && <span className="muted">{description}</span>}
        <ul>
          {Object.keys(value).slice(0, MAX_CHILDREN).map(key => (
            <Node key={key} name={key} value={value[key]} schema={childSchema} depth={depth + 1} highlight={highlight} />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className={isPrimary ? "value-leaf is-primary" : "value-leaf"}>
      {name !== undefined && <span className="value-name">{name}</span>}
      {description && <span className="muted">{description}</span>}
      <code>{scalar(value)}</code>
    </li>
  );
}

export function ValueTree({ value, schema, highlight }: {
  value: unknown;
  schema: JsonSchemaFragment | undefined;
  highlight: readonly string[];
}): JSX.Element {
  return <ul className="value-tree">{<Node name={undefined} value={value} schema={schema} depth={0} highlight={highlight} />}</ul>;
}
